// src/components/Procurement/FifoSettlementPreview.jsx
// Prompt 197 Part B — shared FIFO settlement preview. Display-only over computeAged's open items
// (GET /api/reports/aged-payables). Shows how `amount` settles a supplier's outstanding orders
// oldest-first, highlighting `operationId` (the order in context, if any). Used by every Make
// Payment surface: GIT inline (SupplierPaymentPanel), Payments page, Data Entry Finance.
import React, { useEffect, useState } from 'react';
import api from '../../services/api';

import { Emo, emoText } from '../../shell/EmojiIcons';
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('fr-FR');

export default function FifoSettlementPreview({ partnerId, accountId, amount, operationId }) {
  const [items, setItems] = useState(null);
  const [crossLC, setCrossLC] = useState([]);   // 262: landed-cost dues on OTHER accounts (non-payable here)

  useEffect(() => {
    if (!partnerId && !accountId) { setItems(null); setCrossLC([]); return; }
    let live = true;
    api.request('/reports/aged-payables')
      .then(rows => {
        if (!live) return;
        const allItems = (rows || []).flatMap(r => r.items || []);
        // aged-payables is ACCOUNT-scoped (one FIFO queue per account). A payment settles ONE
        // account, so when we know the account, scope STRICTLY to it — never OR-in the partner,
        // which can span several accounts and interleave unrelated queues. Fall back to partner
        // only when no account is known (e.g. credit notes).
        const match = accountId
          ? (it) => it.account_id === accountId
          : (it) => it.partner_id === partnerId;
        const open = allItems
          .filter(it => it.operation_id && (Number(it.amount) || 0) > 0.005 && match(it))
          // Match computeAged's total order exactly: date, then operation_id (proxy for row id).
          .sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))
            || String(a.operation_id || '').localeCompare(String(b.operation_id || '')));
        setItems(open);
        // 262 (258/#3) — DISCOVERABILITY, not payability. The MAKE-PAYMENT-NO-LC item was filed as "LC
        // isn't payable from these surfaces", but it IS — from the LC supplier's OWN account/queue. The
        // real gap is that from THIS (goods-supplier, one-account) surface there's no signal that an
        // order's landed-cost dues sit on a SEPARATE LC-supplier account. Surface them as a NON-PAYABLE
        // note (the account-strict invariant stands: this payment settles ONE account and cannot pay
        // these). Only when we know the account (the lump-sum case); never OR-in the partner.
        if (accountId) {
          const openOps = new Set(open.map(it => it.operation_id));
          const cross = allItems.filter(it => it.operation_id && it.subtype === 'landed_cost'
            && (Number(it.amount) || 0) > 0.005 && it.account_id !== accountId
            && (operationId ? it.operation_id === operationId : openOps.has(it.operation_id)));
          const byLc = new Map();
          for (const it of cross) {
            const k = it.partner_id || it.account_id;
            if (!byLc.has(k)) byLc.set(k, { name: it.partner_name || 'Landed-cost supplier', amount: 0 });
            byLc.get(k).amount += Number(it.amount) || 0;
          }
          setCrossLC([...byLc.values()]);
        } else setCrossLC([]);
      })
      .catch(() => { if (live) { setItems(null); setCrossLC([]); } });
    return () => { live = false; };
  }, [partnerId, accountId, operationId]);

  // 262 — the NON-PAYABLE note. Deliberately styled UNLIKE the payable rows (dashed amber, no icons, no
  // select control) so it can never read as something this payment settles; its only action is a hint to
  // switch to the LC supplier.
  const crossBanner = crossLC.length > 0 ? (
    <div role="note" style={{ marginBottom: 12, border: '1px dashed #c47d10', borderLeft: '3px solid #c47d10', borderRadius: 6, background: '#fdf6ec', padding: '9px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.03em' }}><Emo name="warn" /> Landed-cost dues on a separate account — not payable here</div>
      {crossLC.map((c, i) => (
        <div key={i} style={{ fontSize: 11.5, color: '#7c5310', fontVariantNumeric: 'tabular-nums', padding: '1px 0' }}>
          <b>{fmt(c.amount)}</b> owed to <b>{c.name}</b> — pay it by switching to <b>{c.name}</b> in Make Payment.
        </div>
      ))}
      <div style={{ fontSize: 10.5, color: '#a97b3a', marginTop: 3, fontStyle: 'italic' }}>
        This payment settles only the account above — it cannot pay these.
      </div>
    </div>
  ) : null;

  if (items === null) return null; // not loaded yet / no partner+account to query
  if (items.length === 0) {
    // Queried, but nothing open — e.g. Sonal, whose purchases were all paid in cash. Show a clear
    // signal rather than a blank space so the user knows there's genuinely nothing to settle.
    // 262: still surface the cross-account LC note even when this account has nothing open.
    return (
      <>
        {crossBanner}
        <div style={{ marginBottom: 12, border: '1px solid var(--line)', borderRadius: 6, background: '#fbfdfb', padding: '8px 12px', fontSize: 11, color: 'var(--muted)' }}>
          No outstanding payable orders to settle — nothing is open on credit (any purchases were paid in cash).
        </div>
      </>
    );
  }

  // Walk the entered amount through the outstanding orders oldest-first.
  let rem = Number(amount) || 0;
  const walk = items.map(it => {
    const owed = Number(it.amount) || 0;
    const covered = Math.max(0, Math.min(owed, rem));
    rem = Math.max(0, rem - covered);
    const status = covered >= owed - 0.005 ? 'full' : covered > 0.005 ? 'partial' : 'none';
    return { ...it, owed, covered, status };
  });
  // Amount needed to fully settle THIS order = cumulative outstanding up to & including it.
  let cum = 0, toFullyPayThis = null;
  for (const it of items) { cum += Number(it.amount) || 0; if (it.operation_id === operationId) { toFullyPayThis = cum; break; } }

  return (
    <>
    {crossBanner}
    <div style={{ marginBottom: 12, border: '1px solid var(--line)', borderRadius: 6, background: '#fbfdfb', padding: '10px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>
        This supplier has outstanding orders (settled oldest first):
      </div>
      {walk.map((w, wi) => {
        const isThis = w.operation_id === operationId;
        const icon = w.status === 'full' ? ":check:" : w.status === 'partial' ? '~' : '⊘';
        const iconColor = w.status === 'full' ? '#16a34a' : w.status === 'partial' ? '#c47d10' : '#9ca3af';
        const detail = w.status === 'full' ? 'fully paid'
          : w.status === 'partial' ? `${fmt(w.covered)} partial`
          : '0 (payment exhausted)';
        return (
          <div key={`${w.operation_id}-${wi}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '2px 0', fontVariantNumeric: 'tabular-nums', background: isThis ? '#eef6f0' : 'transparent', borderRadius: 3, fontWeight: isThis ? 700 : 400 }}>
            <span style={{ color: iconColor, width: 12, textAlign: 'center' }}>{emoText(icon)}</span>
            <span style={{ color: 'var(--muted)', minWidth: 128 }}>{w.operation_id}</span>
            <span style={{ minWidth: 70, textAlign: 'right' }}>{fmt(w.owed)}</span>
            <span style={{ color: 'var(--muted)' }}>→ {detail}</span>
            {isThis && <span style={{ color: '#1b4332' }}>← this order</span>}
          </div>
        );
      })}
      {toFullyPayThis != null && (
        <div style={{ fontSize: 11, color: '#1b4332', marginTop: 6, fontWeight: 600 }}>
          To fully pay this order: pay {fmt(toFullyPayThis)} (covers all prior outstanding).
        </div>
      )}
    </div>
    </>
  );
}
